import { FreeSpinsInfoEvent, SpinResponse, SpecialSymbolGroupType } from '@cgs/data-access';
import {
  AbstractGameConfig,
  ComponentIndex,
  ComponentNames,
  GAME_STATE_MACHINE_PROVIDER_TOKEN,
  GameStateMachine,
  IconEnumerator,
  IconEnumeratorComponent,
  IGameConfigProvider,
  IGameStateMachineProvider,
  IReelsConfig,
  IReelsConfigProvider,
  ISlotGameEngineProvider,
  MultiSceneReelsEngineV2,
  SLOT_GAME_SETTINGS_TOKEN,
  T_IconEnumeratorComponent,
  T_IGameConfigProvider,
  T_IReelsConfigProvider,
  T_ISlotGameEngineProvider,
} from '@cgs/machines';
import { CorsicaGameSettings } from '../../corsica-game-settings';

import {
  Container,
  EmptyAction,
  FunctionAction,
  IntervalAction,
  ParallelAction,
  SceneObject,
  SequenceAction,
} from '@cgs/wge';
import {
  CORSICA_FREE_SPINS_TOTAL_COMPONENT_TOKEN,
  CorsicaFreeSpinsTotalComponent,
} from './corsica-free-spins-total.component';
import { FreeSpinEventType } from './corsica-free-spins.types';
import {
  createIconValueDescription,
  PanelStates,
} from './corsica-icon-with-values/corsica-icon-values.helper';
import { IconValueDescription } from './corsica-icon-with-values/corsica-icon-values.types';

export const CORSICA_FREE_SPINS_START_COMPONENT_TOKEN = Symbol('CorsicaFreeSpinsStartComponent');

export class CorsicaFreeSpinsStartComponent {
  private readonly gameStateMachine: GameStateMachine<SpinResponse>;
  private readonly reelsEngine: MultiSceneReelsEngineV2;
  private readonly reelsConfig: IReelsConfig;
  private readonly gameSettings: CorsicaGameSettings;

  private readonly totalComponent: CorsicaFreeSpinsTotalComponent;
  private readonly enumerator: IconEnumerator;
  private readonly config: AbstractGameConfig;

  private readonly iconValueComponentIndex: ComponentIndex;
  private readonly iconNodeComponentIndex: ComponentIndex;
  private readonly drawableIndexComponentIndex: ComponentIndex;

  constructor(container: Container) {
    this.gameStateMachine = container.resolve<IGameStateMachineProvider>(
      GAME_STATE_MACHINE_PROVIDER_TOKEN
    ).gameStateMachine;

    this.reelsEngine = container.resolve<ISlotGameEngineProvider>(T_ISlotGameEngineProvider)
      .reelsEngine as MultiSceneReelsEngineV2;

    this.gameSettings = container.resolve<CorsicaGameSettings>(SLOT_GAME_SETTINGS_TOKEN);

    this.reelsConfig = container.resolve<IReelsConfigProvider>(T_IReelsConfigProvider).reelsConfig;

    this.totalComponent = container.resolve<CorsicaFreeSpinsTotalComponent>(
      CORSICA_FREE_SPINS_TOTAL_COMPONENT_TOKEN
    );

    this.enumerator =
      container.resolve<IconEnumeratorComponent>(T_IconEnumeratorComponent).iconsEnumerator;

    this.config = container.resolve<IGameConfigProvider>(T_IGameConfigProvider).gameConfig;

    this.iconValueComponentIndex = this.reelsEngine.entityEngine.getComponentIndex(
      ComponentNames.IconValue
    );

    this.iconNodeComponentIndex = this.reelsEngine.entityEngine.getComponentIndex(
      ComponentNames.IconNode
    );

    this.drawableIndexComponentIndex = this.reelsEngine.entityEngine.getComponentIndex(
      ComponentNames.DrawableIndex
    );

    this.gameStateMachine.beginFreeSpins.appendLazyAnimation(() => this.replaceReels());
    this.gameStateMachine.beginFreeSpins.appendLazyAnimation(() => this.showMultiplier());

    this.gameStateMachine.freeSpinsRecovery.addLazyAnimationToBegin(() => this.recoveryFreeSpins());
  }

  private replaceReels(): IntervalAction {
    if (
      this.gameStateMachine.curResponse.freeSpinsInfo &&
      this.gameStateMachine.curResponse.freeSpinsInfo.event === FreeSpinsInfoEvent.FreeSpinsAdded
    ) {
      return new EmptyAction();
    }

    const specGroups = this.gameStateMachine.curResponse.specialSymbolGroups || [];

    const startLine = -this.reelsEngine.internalConfig.additionalUpLines;
    const endLine =
      this.reelsConfig.lineCount + this.reelsEngine.internalConfig.additionalBottomLines;

    const freeSpinNonValuableIconId = this.gameSettings.getNonValueFreeSpinIcon();

    const animations: IntervalAction[] = [];

    animations.push(
      new FunctionAction(() => {
        for (let reel = 0; reel < this.reelsConfig.reelCount; reel++) {
          for (let line = startLine; line < endLine; line++) {
            const entity = this.reelsEngine.getStoppedEntities(reel, line)[0];

            if (!entity) {
              continue;
            }

            const iconScene = entity.get<SceneObject>(this.iconNodeComponentIndex);

            let replaceIconNodeScene = this.reelsEngine.iconResourceProvider.getIconNodes(
              `icon_${freeSpinNonValuableIconId}`
            )![0];

            let replaceIconId = freeSpinNonValuableIconId;

            if (iconScene.id === `icon_${this.gameSettings.getScatterId()}`) {
              replaceIconId = this.gameSettings.getBonusId();

              replaceIconNodeScene = this.reelsEngine.iconResourceProvider.getIconNodes(
                `icon_${this.gameSettings.getBonusId()}`
              )![0];
            }

            entity.set(this.iconNodeComponentIndex, replaceIconNodeScene);
            entity.set(this.drawableIndexComponentIndex, replaceIconId);

            this.reelsEngine.entityCacheHolder.replaceEntities(reel, line, [entity]);
          }
        }
      })
    );

    animations.push(
      new FunctionAction(() => {
        specGroups
          .filter(
            (group) =>
              group.type === SpecialSymbolGroupType.Multiplier &&
              group.subType === FreeSpinEventType.Init
          )
          .forEach((bonusSymbol) => {
            if (!bonusSymbol.positions?.length) {
              return;
            }

            const symbolPosition = bonusSymbol.positions[0];

            const reel = this.reelsEngine.getReelByPosition(symbolPosition);
            const line = this.reelsEngine.getLineByPosition(symbolPosition);

            const entity = this.reelsEngine.getStoppedEntities(reel, line)[0];

            if (!entity) {
              return;
            }

            const valueDescription = createIconValueDescription(
              bonusSymbol.totalWin!,
              symbolPosition,
              'multText'
            );

            entity.addComponent(ComponentNames.IconValue, valueDescription);
            entity.addComponent(ComponentNames.NeedUpdate, true);
          });
      })
    );

    animations.push(
      new FunctionAction(() => {
        for (let reel = 0; reel < this.reelsConfig.reelCount; reel++) {
          for (let line = 0; line < this.reelsConfig.lineCount; line++) {
            this.enumerator.setMappedWinIndex(reel, line, 0);
          }
        }
        this.enumerator.setSpinedReels(this.config.freeSpinConfig.spinedReels);
      })
    );

    return new SequenceAction(animations);
  }

  private showMultiplier(): IntervalAction {
    if (
      this.gameStateMachine.curResponse.freeSpinsInfo &&
      this.gameStateMachine.curResponse.freeSpinsInfo.event !== FreeSpinsInfoEvent.FreeSpinsStarted
    ) {
      return new EmptyAction();
    }

    const specGroups = this.gameStateMachine.curResponse.specialSymbolGroups || [];

    const bonusSpecGroups = specGroups.filter(
      (group) =>
        group.type === SpecialSymbolGroupType.Multiplier &&
        group.subType === FreeSpinEventType.Bonus
    );

    if (!bonusSpecGroups.length) {
      return new EmptyAction();
    }

    const animations: IntervalAction[] = [new EmptyAction()];

    bonusSpecGroups.forEach((group) => {
      if (!group.positions?.length) {
        return;
      }

      const symbolPosition = group.positions[0];

      const reel = this.reelsEngine.getReelByPosition(symbolPosition);
      const line = this.reelsEngine.getLineByPosition(symbolPosition);

      const entity = this.reelsEngine.getStoppedEntities(reel, line)[0];

      const valueDescription = entity.get<IconValueDescription>(this.iconValueComponentIndex);

      const iconNode = entity.get<SceneObject>(this.iconNodeComponentIndex);

      if (!entity || !valueDescription || !iconNode) {
        return;
      }

      animations.push(
        new SequenceAction([
          new FunctionAction(() => {
            this.reelsEngine.startAnimation(entity, 'start');
          }),
          new EmptyAction().withDuration(iconNode.stateMachine?.getStateDuration('start') || 0),
          new FunctionAction(() => {
            this.reelsEngine.startAnimation(entity, 'middle');
          }),
          new EmptyAction().withDuration(0.25),
          new FunctionAction(() => {
            valueDescription.panelAnimState = PanelStates.Anim;
            valueDescription.value = group.totalWin! + group.collectCount!;

            entity.addComponent(ComponentNames.IconValue, valueDescription);
            entity.addComponent(ComponentNames.NeedUpdate, true);

            this.totalComponent.addToTotal(group.collectCount!);
          }),

          new FunctionAction(() => {
            this.reelsEngine.startAnimation(entity, 'end');
          }),
          new EmptyAction().withDuration(iconNode.stateMachine?.getStateDuration('end') || 0),
        ])
      );
    });

    return new ParallelAction(animations);
  }

  private recoveryFreeSpins(): IntervalAction {
    const startLine = 0 - this.reelsEngine.internalConfig.additionalUpLines;
    const endLine =
      this.reelsConfig.lineCount + this.reelsEngine.internalConfig.additionalBottomLines;

    const freeSpinNonValuableIcon = this.gameSettings.getNonValueFreeSpinIcon();

    const specGroups = this.gameStateMachine.curResponse.specialSymbolGroups || [];

    return new FunctionAction(() => {
      for (let reel = 0; reel < this.reelsConfig.reelCount; reel++) {
        for (let line = startLine; line < endLine; line++) {
          const entity = this.reelsEngine.getStoppedEntities(reel, line)[0];
          if (entity) {
            const drawableIndex = entity.get<number>(this.drawableIndexComponentIndex);

            if (this.gameSettings.getSpecialFreeSpinIcon().includes(drawableIndex)) {
              continue;
            }

            const iconScene = this.reelsEngine.iconResourceProvider.getIconNodes(
              `icon_${freeSpinNonValuableIcon}`
            )![0];

            entity.set(this.iconNodeComponentIndex, iconScene);
            entity.set(this.drawableIndexComponentIndex, freeSpinNonValuableIcon);

            this.reelsEngine.entityCacheHolder.replaceEntities(reel, line, [entity]);
          }
        }
      }

      specGroups
        .filter(
          (group) =>
            group.type === SpecialSymbolGroupType.Multiplier &&
            group.subType === FreeSpinEventType.Init
        )
        .forEach((group) => {
          if (!group.positions?.length) {
            return;
          }

          const symbolPosition = group.positions[0];

          const reel = this.reelsEngine.getReelByPosition(group.positions[0]);
          const line = this.reelsEngine.getLineByPosition(group.positions[0]);

          const entity = this.reelsEngine.getStoppedEntities(reel, line)[0];

          if (!entity) {
            return;
          }

          const valueDescription = createIconValueDescription(
            group.totalWin!,
            symbolPosition,
            'multText'
          );

          entity.addComponent(ComponentNames.IconValue, valueDescription);
          entity.addComponent(ComponentNames.NeedUpdate, true);
        });
    });
  }
}
